/**
 * BEA / RIMS II attribution block.
 *
 * Renders the legally-required attribution (and, for RIMS II, the non-
 * endorsement disclaimer) underneath any UI surface that displays RIMS II
 * multiplier data or BEA API data. See packages/shared/src/attributions.ts
 * for the source-of-truth strings and the compliance contract.
 *
 * Usage:
 *   <BEAAttribution variant="rims" />   // after any multiplier display
 *   <BEAAttribution variant="bea" />    // after non-RIMS BEA API data
 *
 * Never swap in a BEA or DOC logo. Never reword to imply endorsement.
 */

import { BEA_ATTRIBUTION, RIMS_II_ATTRIBUTION, RIMS_II_DISCLAIMER } from '@develio/shared';

interface BEAAttributionProps {
  /** 'rims' shows both attribution + disclaimer. 'bea' shows attribution only. */
  readonly variant?: 'rims' | 'bea';
  /** Optional extra spacing / border when used standalone rather than inline. */
  readonly className?: string;
}

export function BEAAttribution({ variant = 'rims', className = '' }: BEAAttributionProps) {
  if (variant === 'bea') {
    return (
      <p
        data-testid="bea-attribution"
        data-attribution="bea"
        className={`text-xs italic text-slate-400 ${className}`}
      >
        {BEA_ATTRIBUTION}
      </p>
    );
  }
  return (
    <div
      data-testid="bea-attribution"
      data-attribution="rims"
      className={`text-xs italic leading-snug text-slate-400 ${className}`}
    >
      <p>{RIMS_II_ATTRIBUTION}</p>
      <p>{RIMS_II_DISCLAIMER}</p>
    </div>
  );
}
